import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const navItems = [
  { to: '/admin/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/admin/users', icon: '👥', label: 'Usuários', adminOnly: true },
  { to: '/admin/freight', icon: '🚚', label: 'Fretes', permKey: 'fretes' },
  { to: '/admin/correios', icon: '🧪', label: 'Simulador de Fretes', permKey: 'simulador_fretes' },
  { to: '/admin/etiquetas', icon: '🏷️', label: 'Emissão de Etiquetas', permKey: 'etiquetas' },
  { to: '/admin/tokens', icon: '🔑', label: 'Tokens de API', permKey: 'tokens_api' },
];

const ROLE_LABELS = { admin: 'Administrador', lojista: 'Lojista' };

export default function AdminLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const visibleNavItems = navItems.filter((item) => {
    if (item.adminOnly) return user?.role === 'admin';
    if (user?.role === 'admin') return true;
    if (item.permKey) return user?.permissions?.[item.permKey] !== false;
    return true;
  });

  function handleLogout() {
    logout();
    toast.success('Sessão encerrada');
    navigate('/login');
  }

  return (
    <div style={styles.root}>
      {/* Sidebar */}
      <aside style={{ ...styles.sidebar, width: sidebarOpen ? '260px' : '72px' }}>
        <div style={styles.sidebarHeader}>
          <div style={styles.logoBox}>
            <span style={styles.logoIcon}>📦</span>
          </div>
          {sidebarOpen && (
            <div>
              <div style={styles.brandName}>BringHub Frete</div>
              <div style={styles.brandTag}>{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          )}
        </div>

        <nav style={styles.nav}>
          <div style={styles.navSection}>
            {sidebarOpen && <span style={styles.navSectionLabel}>Menu Principal</span>}
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({
                  ...styles.navItem,
                  ...(isActive ? styles.navItemActive : {}),
                  justifyContent: sidebarOpen ? 'flex-start' : 'center',
                })}
              >
                <span style={styles.navIcon}>{item.icon}</span>
                {sidebarOpen && <span style={styles.navLabel}>{item.label}</span>}
              </NavLink>
            ))}
          </div>
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={{ ...styles.userInfo, justifyContent: sidebarOpen ? 'flex-start' : 'center' }}>
            <div style={styles.avatar}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            {sidebarOpen && (
              <div style={styles.userDetails}>
                <div style={styles.userName}>{user?.name}</div>
                <div style={styles.userRole}>{ROLE_LABELS[user?.role] || user?.role}</div>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            style={{ ...styles.logoutBtn, justifyContent: sidebarOpen ? 'flex-start' : 'center' }}
            title="Sair"
          >
            <span>🚪</span>
            {sidebarOpen && <span>Sair</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div style={styles.main}>
        <header style={styles.header}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={styles.toggleBtn}>
            {sidebarOpen ? '◀' : '▶'}
          </button>
          <div style={styles.headerRight}>
            <span style={styles.headerUser}>Olá, {user?.name?.split(' ')[0]}</span>
          </div>
        </header>
        <main style={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const styles = {
  root: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
  },
  sidebar: {
    background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.25s ease',
    overflow: 'hidden',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    height: '100vh',
  },
  sidebarHeader: {
    padding: '24px 16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  logoBox: {
    width: '40px',
    height: '40px',
    background: 'rgba(255,255,255,0.15)',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  logoIcon: { fontSize: '20px' },
  brandName: { fontSize: '16px', fontWeight: '700', color: '#ffffff', whiteSpace: 'nowrap' },
  brandTag: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px' },
  nav: { flex: 1, padding: '16px 10px', overflowY: 'auto' },
  navSection: { display: 'flex', flexDirection: 'column', gap: '4px' },
  navSectionLabel: {
    fontSize: '10px',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '1.2px',
    padding: '0 8px',
    marginBottom: '8px',
    marginTop: '4px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    color: 'rgba(255,255,255,0.75)',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.15s',
    textDecoration: 'none',
  },
  navItemActive: {
    background: 'rgba(255,255,255,0.15)',
    color: '#ffffff',
  },
  navIcon: { fontSize: '18px', flexShrink: 0 },
  navLabel: { whiteSpace: 'nowrap' },
  sidebarFooter: {
    padding: '12px 10px 16px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '8px',
    background: 'rgba(255,255,255,0.08)',
  },
  avatar: {
    width: '32px',
    height: '32px',
    background: 'rgba(255,255,255,0.25)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '700',
    color: '#ffffff',
    flexShrink: 0,
  },
  userDetails: {},
  userName: { fontSize: '13px', fontWeight: '600', color: '#ffffff', whiteSpace: 'nowrap' },
  userRole: { fontSize: '11px', color: 'rgba(255,255,255,0.5)' },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: 'none',
    border: 'none',
    borderRadius: '8px',
    color: 'rgba(255,255,255,0.6)',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background 0.15s',
    width: '100%',
  },
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  header: {
    height: '64px',
    background: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  toggleBtn: {
    background: 'none',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#6b7280',
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  headerUser: { fontSize: '14px', fontWeight: '500', color: '#374151' },
  content: { flex: 1, padding: '28px', overflowY: 'auto' },
};
